
<i18n>
{
	"en": {
		"failedToAddOrderToTrip": "Failed to add order to trip {number}",
		"failedToAddOrderToTransporter": "Failed to add order to transporter",
		"orderAddedToTrip": "Order added to trip {number}",
		"orderAddedToTransporter": "Order added to transporter",
		"orderAlreadyAdded": "A selected order is already part of the selected trip.",
		"validationErrors": "Trip is currently invalid, please edit trip before adding orders.",
		"unableToUpdateTrip": "Could not update trip",
		"invalidMaxLoadingSequenceNo": "The existing orders on the trip have an invalid loading sequence. The corrupt data needs to be fixed first.",
		"missingEmailAddress": "Email address is missing."
	},
	"no": {
		"failedToAddOrderToTrip": "Kunne ikke legge til oppdrag på turen {number}",
		"failedToAddOrderToTransporter": "Kunne ikke legge til oppdrag til transportøren",
		"orderAddedToTrip": "Oppdrag lagt til tur {number}",
		"orderAddedToTransporter": "Oppdrag lagt til transportøren",
		"orderAlreadyAdded": "Et valgt oppdrag er allerede en del av den valgte turen.",
		"validationErrors": "Turen er ugyldig. Rediger turen før du legger til oppdrag.",
		"unableToUpdateTrip": "Kunne ikke oppdatere tur",
		"invalidMaxLoadingSequenceNo": "De eksisterende oppdragene har ugyldig lastesekvens. Korrupt data må fikses først.",
		"missingEmailAddress": "Email-adresse mangler"
	}
}
</i18n>
<template>
	<t-resizable-horizontal-panel
		:localStorageId="localStorageId"

		style="height:100%; width:100%"
	>
		<template #left="{ leftWidth }">
			<order-search-list-main

				:containerWidth="leftWidth"

				@loadSavedSearch="loadSavedSearch"
				@saveSearch="toggleSaveSearch(true)"
				@showAddToTrip="showAddToTrip"
				@showAddToTransporter="showAddToTransporter"
			/>
		</template>
		<template #right="{ rightWidth }">
			<trip-search-list-main

				:containerWidth="rightWidth"

				@saveSearch="toggleSaveSearch(true)"
				@showAddToTrip="showAddToTrip"
				@showAddToTransporter="showAddToTransporter"
				@showMoveLoadedOrder="showMoveLoadedOrder"
			/>
		</template>

		<div slot="dialogs">
			<AddOrderToTripLocationsSelect
				:isShown="addToTripVisible"
				:selectedTrip="selectedTrip"
				:orders="selectedOrders"
				:disableSubmit="addToTripProcessing"

				@close="addToTripVisible = false"
				@addToTrip="onAddOrdersToTrip"
			/>
			<AddOrderToTransporterSelect
				:isShown="addToTransporterVisible"
				:selectedTransporterOrder="selectedTrip"
				:orders="selectedOrders"
				:disableSubmit="addToTransporterProcessing"

				@close="addToTransporterVisible = false"
				@addToTransporter="onAddOrdersToTransporter"
			/>

			<MoveOrderToDifferentTripSelect
				:isShown="moveLoadedOrderVisible"
				:currentTrip="selectedTrip"
				:order="selectedOrder"
				:disableSubmit="moveLoadedOrderProcessing"

				@close="moveLoadedOrderVisible = false"
				@moveOrder="onMoveOrderToDifferentTrip"
			/>

			<SaveSearch
				:isShown="saveSearchVisible"
				:orderFilter="orderFilter"
				:tripFilter="tripFilter"
				:loadedSearch="savedSearch"

				@close="toggleSaveSearch(false)"
				@delete="onDeleteSavedSearch"
			/>
		</div>
	</t-resizable-horizontal-panel>
</template>

<script>
import {
	mapDynamicState, mapDynamicGetters, mapDynamicActions
} from '@/core/mapDynamic';
import OrderSearchListMain from './OrderSearch/OrderSearchListMain.vue';
import TripSearchListMain from './TripSearch/TripSearchListMain.vue';
import AddOrderToTripLocationsSelect from '@/modules/loadPlanningList/modals/AddOrderToTripLocationsSelect';
import AddOrderToTransporterSelect from '@/modules/loadPlanningList/modals/AddOrderToTransporterSelect';
import MoveOrderToDifferentTripSelect from '@/modules/loadPlanningList/modals/MoveOrderToDifferentTripSelect';
import SaveSearch from '@/modules/saveLoadPlanningSearch/SaveSearch';
import { mapState as savedSearchState } from '@/modules/saveLoadPlanningSearch/store/saveLoadPlanningSearch.store.js';
import TResizableHorizontalPanel from '@/components/evergreen/timpex/layers/TResizableHorizontalPanel';

export default {
	name: 'loadPlanningListMain',
	components: {
		TResizableHorizontalPanel,
		TripSearchListMain,
		OrderSearchListMain,
		SaveSearch,
		AddOrderToTripLocationsSelect,
		AddOrderToTransporterSelect,
		MoveOrderToDifferentTripSelect
	},
	inject: ['namespace'],
	data() {
		return {
			mounting: true,
			saveSearchVisible: false,
			addToTripVisible: false,
			addToTripProcessing: false,
			addToTransporterVisible: false,
			addToTransporterProcessing: false,
			moveLoadedOrderVisible: false,
			moveLoadedOrderProcessing: false,
			selectedOrders: [],
			selectedTrip: {},
			selectedOrder: {}
		};
	},
	computed: {
		...mapDynamicState('computedStoreNamespace',['loadedSearch']),
		...mapDynamicGetters('computedStoreNamespace',[
			'orderFilter',
			'tripFilter',
			'ordersLoading',
			'tripsLoading'
		]),
		...savedSearchState(['savedSearches']),
		computedStoreNamespace() {
			return `loadPlanning/${this.namespace.id}`;
		},
		localStorageId() {
			return `loadPlanningList/${this.namespace.id}`;
		},
		savedSearch() {
			if (Array.isArray(this.savedSearches))
				return this.savedSearches.find(x => x.id === this.namespace.id);
			return {};
		},
	},
	watch: {
		savedSearch(newValue,oldValue) {
			if (this.mounting) return;
			if (
				(
					oldValue === undefined
					&& newValue !== undefined
				)
				|| (
					oldValue
					&& newValue
					&& oldValue.id !== newValue.id
				)
			) {
				this.loadSavedAndSearch();

			} else if (
				newValue === undefined
				&& this.namespace.id !== -1
			) {
				this.sendToDefaultSearch();
			}
		}
	},
	mounted() {
		if ( !this.savedSearch && this.namespace.id !== -1 ) {
			this.sendToDefaultSearch();
		} else if ( this.savedSearch && this.namespace.id !== this.loadedSearch.id ) {
			this.loadSavedAndSearch();
		} else if ( this.namespace.id === this.loadedSearch.id ) {
			this.fetchOrdersAndTrips();
		}
		this.mounting = false;
	},
	methods: {
		...mapDynamicActions('computedStoreNamespace',[
			'loadSavedSearch',
			'fetchOrdersAndTrips',
			'addOrdersToTrip',
			'addOrdersToTransporter',
			'moveOrderToDifferentTrip'
		]),
		toggleSaveSearch(value) {
			this.saveSearchVisible = value;
		},
		sendToDefaultSearch() {
			let defaultView = localStorage.getItem(`loadPlanning/-1/defaultView`);

			this.$router.replace({ name: defaultView === 'map' ? 'loadPlanningMap' : 'loadPlanningList', });
		},
		loadSavedAndSearch() {
			if (this.namespace.id === this.loadedSearch.id)
				return;

			this.loadSavedSearch(this.savedSearch);

			this.fetchOrdersAndTrips();
		},
		onDeleteSavedSearch() {

			this.toggleSaveSearch(false);

			this.$router.push({ name: 'loadPlanningList' });
		},
		showAddToTrip({ orders, trip }) {
			// Trip is external transporter
			if (trip && trip.type.internalCode === 'external') {
				return;
			}
			this.selectedOrders = orders;
			this.selectedTrip = trip;
			this.addToTripVisible = true;
		},
		showAddToTransporter({ orders, trip }) {

			this.selectedOrders = orders;
			this.selectedTrip = trip;
			this.addToTransporterVisible = true;
		},
		showMoveLoadedOrder({ order, trip }) {

			this.selectedOrder = order;
			this.selectedTrip = trip;
			this.moveLoadedOrderVisible = true;
		},
		async onAddOrdersToTrip({ despatchOption, deliveryOption, etd, eta, customDespatchParty, customDeliveryParty }) {
			this.addToTripProcessing = true;
			try {
				await this.addOrdersToTrip({ orders: this.selectedOrders, tripId: this.selectedTrip.id, despatchOption, deliveryOption, etd, eta, customDespatchParty, customDeliveryParty });

				this.$notify({
					type: 'success',
					title: `${this.$t('orderAddedToTrip', { number: this.selectedTrip.tripNumber })}`
				});
				this.addToTripVisible = false;

			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t(e.message || 'failedToAddOrderToTrip', { number: this.selectedTrip.tripNumber }),
					duration: -1
				});
			}
			this.addToTripProcessing = false;
		},
		async onAddOrdersToTransporter(body) {
			this.addToTransporterProcessing = true;
			try {
				await this.addOrdersToTransporter({ ...body, orders: this.selectedOrders });

				this.$notify({
					type: 'success',
					title: `${this.$t('orderAddedToTransporter')}`
				});
				this.addToTransporterVisible = false;

			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t(e.message || 'failedToAddOrderToTransporter'),
					duration: -1
				});
			}
			this.addToTransporterProcessing = false;
		},
		async onMoveOrderToDifferentTrip(data) {

			this.moveLoadedOrderProcessing = true;
			try {
				await this.moveOrderToDifferentTrip(data);

				this.$notify({
					type: 'success',
					title: `${this.$t('orderAddedToTrip', { number: data.newTrip.tripNumber })}`
				});
				this.moveLoadedOrderVisible = false;

			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t(e.message || 'failedToAddOrderToTrip', { number: data.newTrip.tripNumber }),
					duration: -1
				});
			}
			this.moveLoadedOrderProcessing = false;
		}
	},
};
</script>
